<template>
  <v-card class="ui-module-card ui-module-fade-in-up" color="primary">
    <v-card-text>
      <v-row>
        <v-col cols="4">
          <v-autocomplete
            :value="idEvaluador"
            @change="$emit('update:idEvaluador', $event)"
            rounded
            filled
            dense
            color="secondary"
            background-color="transparent"
            :items="getPersonalLite"
            item-value="idPersonal"
            :item-text="getFullName"
            item-color="secondary"
            :rules="required"
            :loading="loadingPersonalLite"
            :filter="filterPersonal"
          >
            <template v-slot:label>Personal evaluador <span class="red--text">*</span> </template>
            <template v-slot:item="data">
              <v-list-item-avatar>
                <v-icon>mdi-account</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ getFullName(data.item) }}</v-list-item-title>
                <v-list-item-subtitle>{{ data.item.CUIP }} · {{ data.item.puesto }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          <v-menu
            v-model="menuFecha"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :disabled="editMode"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="fechaFormatted"
                append-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                rounded
                filled
                dense
                color="secondary"
                background-color="transparent"
                :rules="required"
                :disabled="editMode"
              >
                <template v-slot:label>Fecha de aplicación <span class="red--text">*</span> </template>
              </v-text-field>
            </template>
            <v-date-picker
              @change="$emit('update:fechaAplicacion', $event)"
              @input="menuFecha = false"
              :disabled="editMode"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="4">
          <BaseSelect
            :value="tipoEvaluacion"
            label="Tipo de evaluación"
            :items="[
              { id: 8, title: 'EVACOM' },
              { id: 9, title: 'EVACOM (Mando medio)' },
            ]"
            :rules="required"
            :disabled="editMode"
            @change="$emit('update:tipoEvaluacion', $event)"
          />
        </v-col>
      </v-row>
      <hr class="my-4" />
      <DatosGeneralesEvaluadoSection :personal="personal" />
      <template v-if="selectedEvaluador">
        <hr class="my-4" />
        <DatosGeneralesEvaluadorSection :personal="selectedEvaluador" />
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { VUETIFY_DATE_FORMAT } from '@/const'
import moment from 'moment'
import BaseSelect from '@/components/base/BaseSelect.vue'
import { mapActions, mapGetters } from 'vuex'
import DatosGeneralesEvaluadoSection from './DatosGeneralesEvaluadoSection.vue'
import DatosGeneralesEvaluadorSection from './DatosGeneralesEvaluadorSection.vue'

export default {
  components: { BaseSelect, DatosGeneralesEvaluadoSection, DatosGeneralesEvaluadorSection },

  props: {
    personal: {
      type: Object,
      required: true,
    },

    tipoEvaluacion: {
      type: Number,
      default: 8,
    },

    fechaAplicacion: {
      type: String,
      default: '',
    },

    idEvaluador: {
      type: Number,
      default: null,
    },

    editMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      menuFecha: false,
      loadingPersonalLite: false,
      required: [(v) => !!v || 'Este campo es requerido'],
    }
  },

  async mounted() {
    this.loadingPersonalLite = true
    try {
      await this.fetchPersonalLite()
    } catch (error) {
      console.error(error)
    } finally {
      this.loadingPersonalLite = false
    }
  },

  computed: {
    ...mapGetters('personal', { getPersonalLite: 'getPersonalLite' }),

    fechaFormatted: function () {
      return this.fechaAplicacion ? moment(this.fechaAplicacion, VUETIFY_DATE_FORMAT).format(this.API_DATE_FORMAT) : ''
    },

    selectedEvaluador: function () {
      return this.getPersonalLite.find((e) => e.idPersonal === this.idEvaluador) ?? null
    },
  },

  methods: {
    ...mapActions('personal', ['fetchPersonalLite']),

    getFullName(persona) {
      if (!persona) return ''
      return [persona.nombre, persona.segundoNombre, persona.apaterno, persona.amaterno].filter(Boolean).join(' ')
    },

    filterPersonal(item, queryText) {
      const q = queryText.toLowerCase()
      const nombre = this.getFullName(item).toLowerCase()
      const cuip = (item.CUIP || '').toLowerCase()
      const puesto = (item.puesto || '').toLowerCase()
      return nombre.includes(q) || cuip.includes(q) || puesto.includes(q)
    },
  },
}
</script>

<style scoped>
::v-deep .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.25) !important;
  background: rgba(16, 16, 30, 0.35) !important;
  box-shadow: none !important;
  transition: all 0.3s ease !important;
}

::v-deep .v-input__slot:hover {
  border-color: rgba(var(--v-secondary-base-rgb), 0.45) !important;
}

::v-deep .v-input--is-focused .v-input__slot {
  border-color: rgba(var(--v-secondary-base-rgb), 0.7) !important;
  background: rgba(16, 16, 30, 0.5) !important;
}

::v-deep .v-input__control {
  background: transparent !important;
  border-radius: 12px;
  border: none !important;
  box-shadow: none !important;
}

::v-deep input,
::v-deep textarea {
  color: #e0e0e0 !important;
}

::v-deep .v-label,
::v-deep .v-input .v-icon {
  color: rgba(var(--v-secondary-base-rgb), 0.75) !important;
}

::v-deep .v-input--is-focused .v-label {
  color: var(--v-secondary-base) !important;
}

/* Autocomplete lista desplegable */
::v-deep .v-list-item__title,
::v-deep .v-list-item__subtitle {
  color: #e0e0e0 !important;
}

/* Textos generales — evitar negro del tema claro */
::v-deep p,
::v-deep h3,
::v-deep .text-subtitle-1,
::v-deep .text-subtitle-2,
::v-deep .text-body-1,
::v-deep .text-body-2,
::v-deep .text-caption {
  color: #e0e0e0 !important;
}

/* Date picker */
::v-deep .v-date-picker-header {
  background: rgba(var(--v-secondary-base-rgb), 0.15) !important;
}

::v-deep .v-date-picker-table .v-btn.v-btn--active {
  background-color: var(--v-secondary-base) !important;
  color: white !important;
}

::v-deep .v-date-picker-table .v-btn:hover {
  background-color: rgba(var(--v-secondary-base-rgb), 0.2) !important;
}

/* Cards internas */
::v-deep .ui-module-card {
  background: rgba(26, 30, 46, 0.6) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.2) !important;
  border-radius: 16px !important;
}

hr {
  border-color: rgba(var(--v-secondary-base-rgb), 0.15) !important;
}
</style>
